import { ref, computed } from 'vue'
import moment from 'moment'
import { getStartOfDay } from '@/utils/helpers'

export const DAYS_IN_WEEK = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const
export const MONTHS_IN_YEAR = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
] as const

export type CalendarOptions = {
  initialDate?: Date | null
  noRange?: boolean
}

export type DateDisplayInfo = {
  previousMonthDates: number[]
  currentMonthDates: number[]
  nextMonthDates: number[]
}

const useCalendar = (options: CalendarOptions = {}) => {
  const currentDate = new Date(Date.now())

  const activeMonth = ref(options.initialDate?.getMonth() ?? currentDate.getMonth())
  const activeYear = ref(options.initialDate?.getFullYear() ?? currentDate.getFullYear())
  const mouseOverTimestamp = ref<number | null>(null)
  const startTimestamp = ref<number | null>(null)
  const endTimestamp = ref<number | null>(null)

  /**
   * Calculates the day index (0-6, Mon-Sun) for a given day of the month
   * using Zeller's congruence algorithm adapted for Gregorian calendar
   */
  const getDayIndex = computed(() => {
    const gregorianMonths: Record<string, number> = {
      January: 13,
      February: 14,
      March: 3,
      April: 4,
      May: 5,
      June: 6,
      July: 7,
      August: 8,
      September: 9,
      October: 10,
      November: 11,
      December: 12
    }

    const gregorianMonthIndex = Object.values(gregorianMonths).find(
      (_, index) => index === activeMonth.value
    ) as number

    const century = Math.floor(activeYear.value / 100)
    let yearOfCentury = activeYear.value % 100

    if (gregorianMonthIndex > 12) {
      yearOfCentury -= 1
    }

    return (dayOfMonth: number): number => {
      const gregorianIndex =
        (dayOfMonth +
          Math.floor((13 * (gregorianMonthIndex + 1)) / 5) +
          yearOfCentury +
          Math.floor(yearOfCentury / 4) +
          Math.floor(century / 4) +
          5 * century) %
        7
      const index = (gregorianIndex + 5) % 7
      return index < 0 ? DAYS_IN_WEEK.length - 1 : index
    }
  })

  /**
   * Returns the number of days in a given month (0-indexed)
   */
  const getNumberOfDays = (month: number): number => {
    if ((month < 7 && month % 2 === 0) || (month >= 7 && month % 2 !== 0)) {
      return 31
    }
    if (month === 1) {
      if (
        activeYear.value % 4 === 0 &&
        (activeYear.value % 100 !== 0 || activeYear.value % 400 === 0)
      ) {
        return 29
      }
      return 28
    }
    return 30
  }

  /**
   * Returns dates to display for the calendar grid
   */
  const getDatesToDisplay = (startDayIndex: number, month: number): DateDisplayInfo => {
    const datesInMonth: number[] = []

    const datesToDisplayFromPreviousMonth = (): number[] => {
      const dates: number[] = []
      let counter = startDayIndex - 1
      const previousMonth = month - 1 < 0 ? 11 : month - 1
      const numberOfDaysInPreviousMonth = getNumberOfDays(previousMonth)
      while (counter >= 0) {
        dates.push(numberOfDaysInPreviousMonth - counter)
        counter--
      }
      return dates
    }

    const datesToDisplayFromNextMonth = (): number[] => {
      const dates: number[] = []
      const activeMonthLastDate = getNumberOfDays(activeMonth.value)
      const activeMonthLastDayIndex = getDayIndex.value(activeMonthLastDate)
      const numberOfDaysToFill = DAYS_IN_WEEK.length - 1 - activeMonthLastDayIndex

      let counter = 1
      while (counter <= numberOfDaysToFill) {
        dates.push(counter)
        counter++
      }
      return dates
    }

    let date = 1
    while (date <= getNumberOfDays(month)) {
      datesInMonth.push(date)
      date++
    }

    return {
      previousMonthDates: datesToDisplayFromPreviousMonth(),
      currentMonthDates: datesInMonth,
      nextMonthDates: datesToDisplayFromNextMonth()
    }
  }

  const activeMonthDatesToDisplay = computed((): DateDisplayInfo => {
    const startDayIndex = getDayIndex.value(1)
    return getDatesToDisplay(startDayIndex, activeMonth.value)
  })

  const showPreviousMonth = (): void => {
    if (activeMonth.value === 0) {
      activeMonth.value = 11
      activeYear.value--
      return
    }
    activeMonth.value--
  }

  const showNextMonth = (): void => {
    if (activeMonth.value === 11) {
      activeMonth.value = 0
      activeYear.value++
      return
    }
    activeMonth.value++
  }

  const selectedStartDate = computed((): number | null => {
    if (!startTimestamp.value) return null
    return new Date(startTimestamp.value).getDate()
  })

  const selectedEndDate = computed((): number | null => {
    if (!endTimestamp.value) return null
    return new Date(endTimestamp.value).getDate()
  })

  /**
   * Determines if a date should be highlighted (in range selection mode)
   */
  const shouldHighlight = (date: number, noRange?: boolean): string => {
    if (noRange) return ''

    const dateTimestamp = new Date(
      `${activeMonth.value + 1}/${date}/${activeYear.value}`
    ).getTime()
    if (!selectedStartDate.value) return ''

    const isDateWithinRange =
      dateTimestamp >= (startTimestamp.value || 0) &&
      dateTimestamp <= (endTimestamp.value || Infinity) &&
      (mouseOverTimestamp.value ?? 0) > selectedStartDate.value &&
      dateTimestamp <= (mouseOverTimestamp.value || 0)

    return isDateWithinRange ? 'highlight' : ''
  }

  /**
   * Determines the selection state class for a date
   */
  const getSelectionClass = (
    date: number,
    noRange?: boolean,
    multiSelect?: boolean,
    selectedDates?: string[]
  ): string => {
    const dateTimestamp = new Date(
      `${activeMonth.value + 1}/${date}/${activeYear.value}`
    ).getTime()
    const dateString = new Date(dateTimestamp).toLocaleDateString()
    const startDateString = new Date(startTimestamp.value ?? 0).toLocaleDateString()
    const endDateString = new Date(endTimestamp.value ?? 0).toLocaleDateString()

    if (multiSelect && Array.isArray(selectedDates)) {
      const selectedTimestamps = selectedDates.map((d: string) =>
        moment(d).startOf('day').valueOf()
      )
      return selectedTimestamps.includes(dateTimestamp) ? 'selected no-range' : ''
    }

    if (dateString === startDateString) {
      return noRange ? 'selected no-range' : 'selected selected-start'
    }

    if (
      dateString === endDateString ||
      (dateTimestamp === mouseOverTimestamp.value &&
        dateTimestamp > (startTimestamp.value ?? 0) &&
        !endTimestamp.value)
    ) {
      return 'selected selected-end'
    }

    return ''
  }

  /**
   * Checks if a date is today
   */
  const isToday = (date: number): boolean => {
    return (
      date === currentDate.getDate() &&
      activeMonth.value === currentDate.getMonth() &&
      activeYear.value === currentDate.getFullYear()
    )
  }

  /**
   * Creates a timestamp for a given date in the active month/year
   */
  const getDateTimestamp = (date: number): number => {
    return new Date(`${activeMonth.value + 1}/${date}/${activeYear.value}`).getTime()
  }

  /**
   * Checks if a date is in the past relative to a reference date
   */
  const isPastDate = (date: number, referenceDate?: number | string): boolean => {
    const cellTimestamp = getDateTimestamp(date)
    const lastActiveDateTimestamp = referenceDate
      ? new Date(referenceDate).getTime()
      : currentDate.getTime()
    const startOfDayCellDate = getStartOfDay(cellTimestamp)
    const startOfDayLastActiveDate = getStartOfDay(lastActiveDateTimestamp)
    return startOfDayCellDate < startOfDayLastActiveDate
  }

  /**
   * Checks if a date is in the future relative to a reference date
   */
  const isFutureDate = (date: number, referenceDate?: number | string): boolean => {
    const cellTimestamp = getDateTimestamp(date)
    const lastActiveDateTimestamp = referenceDate
      ? new Date(referenceDate).getTime()
      : currentDate.getTime()
    const startOfDayCellDate = getStartOfDay(cellTimestamp)
    const startOfDayLastActiveDate = getStartOfDay(lastActiveDateTimestamp)
    return startOfDayCellDate > startOfDayLastActiveDate
  }

  /**
   * Checks if a date exceeds the next dates limit
   */
  const isOverNextDatesLimit = (date: number, limitDays: number): boolean => {
    const cellTimestamp = getDateTimestamp(date)
    const nextDatesLimitTimestamp = currentDate.getTime() + limitDays * 24 * 60 * 60 * 1000
    const startOfDayCellDate = getStartOfDay(cellTimestamp)
    const startOfDayNextDatesLimit = getStartOfDay(nextDatesLimitTimestamp)
    return startOfDayCellDate > startOfDayNextDatesLimit
  }

  /**
   * Handles mouse over events for range selection
   */
  const handleMouseOver = (event: Event): void => {
    if (event instanceof MouseEvent) {
      const destination = event.relatedTarget as HTMLElement
      if (
        event.type === 'mouseleave' &&
        !destination?.classList?.contains('.datepicker-item') &&
        !endTimestamp.value
      ) {
        mouseOverTimestamp.value = null
        return
      }
    }
    if (selectedStartDate.value && !selectedEndDate.value) {
      const targetElement = event.currentTarget as HTMLButtonElement
      mouseOverTimestamp.value = parseInt(targetElement.value, 10)
    }
  }

  /**
   * Resets the calendar to show a specific date's month/year
   */
  const resetToDate = (date: Date | string | null): void => {
    if (!date) return
    const targetDate = typeof date === 'string' ? new Date(date) : date
    activeMonth.value = targetDate.getMonth()
    activeYear.value = targetDate.getFullYear()
  }

  /**
   * Resets selection state
   */
  const resetSelection = (): void => {
    startTimestamp.value = null
    endTimestamp.value = null
    mouseOverTimestamp.value = null
  }

  /**
   * Sets selection from a date value
   */
  const setSelectionFromValue = (value: Date | string | null): void => {
    if (!value) return
    startTimestamp.value = moment(value).toDate().getTime()
  }

  return {
    // Constants
    daysInWeek: DAYS_IN_WEEK,
    monthsInYear: MONTHS_IN_YEAR,
    currentDate,

    // State
    activeMonth,
    activeYear,
    mouseOverTimestamp,
    startTimestamp,
    endTimestamp,

    // Computed
    getDayIndex,
    activeMonthDatesToDisplay,
    selectedStartDate,
    selectedEndDate,

    // Methods
    getNumberOfDays,
    getDatesToDisplay,
    showPreviousMonth,
    showNextMonth,
    shouldHighlight,
    getSelectionClass,
    isToday,
    getDateTimestamp,
    isPastDate,
    isFutureDate,
    isOverNextDatesLimit,
    handleMouseOver,
    resetToDate,
    resetSelection,
    setSelectionFromValue
  }
}

export default useCalendar
